import { useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

const useBooking = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, sendRequest } = useHttp();

  const updateBooking = async (bookingId, update) => {
    return new Promise(async (resolve, reject) => {
      const responseData = await sendRequest({
        url: '/api/v1/booking/' + bookingId,
        method: 'PATCH',
        headers: authCtx.getAuthHeaders(),
        body: { ...update },
      });
      resolve(responseData);
    });
  };

  const getBookingsByEntertainmentDay = async (entertainmentId, date, showCanceled = false) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/booking/by/entertainment/${entertainmentId}/day/${date}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params: {
            showCanceled: showCanceled,
          },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getFilteredBookingsByEntertainmentDay = async (
    entertainmentId,
    date,
    filter,
    showCanceled = false
  ) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/booking/by/entertainment/${entertainmentId}/day/${date}`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: { filter: { ...filter } },
          params: {
            showCanceled: showCanceled,
          },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getResourcesByEntertainmentsDay = async data => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/booking/resources/by/day/${data.date}`,
          method: 'POST',
          body: {
            branches: data.branches,
            entertainments: data.entertainments,
          },
          headers: authCtx.getAuthHeaders(),
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getResourcesBySingleEntertainmentDay = async data => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/booking/resources/by/entertainment/${data.entertainmentId}/day/${data.date}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const createTimeslotBlocker = async data => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: '/api/v1/internalBooking/timeslotBlocker',
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: {
            startTime: data.startTime,
            endTime: data.endTime,
            branchId: data.branchId,
            entertainmentId: data.entertainmentId,
            blockedSlots: data.blockedSlots,
            reasonCategory: data.reasonCategory,
            reasonText: data.reasonText,
            resourceIds: data.resourceIds,
            groupType: data.groupType,
          },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  // Both blocker dialog helpers are reads behind a POST (the grid needs a body).
  // `noAutoStore` keeps a 403 out of the open requests: authenticating a
  // colleague to compute dropdown times helps nobody.
  const previewTimeslotBlockerSnap = async data => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: '/api/v1/internalBooking/timeslotBlocker/preview',
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          noAutoStore: true,
          body: {
            startTime: data.startTime,
            endTime: data.endTime,
            entertainmentId: data.entertainmentId,
            resourceIds: data.resourceIds,
          },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getTimeslotBlockerTimeOptions = async data => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: '/api/v1/internalBooking/timeslotBlocker/timeOptions',
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          noAutoStore: true,
          body: {
            entertainmentId: data.entertainmentId,
            date: data.date,
            resourceIds: data.resourceIds,
          },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  // Submit a blocker request (needs approval before it becomes an actual blocker).
  const createBlockerRequest = async data => {
    return sendRequest({
      url: '/api/v1/internalBooking/blockerRequest',
      method: 'POST',
      headers: authCtx.getAuthHeaders(),
      body: {
        startTime: data.startTime,
        endTime: data.endTime,
        branchId: data.branchId,
        entertainmentId: data.entertainmentId,
        blockedSlots: data.blockedSlots,
        reasonCategory: data.reasonCategory,
        reasonText: data.reasonText,
        resourceIds: data.resourceIds,
        groupType: data.groupType,
      },
    });
  };

  // List blocker requests (approver view), optionally filtered by status.
  const getBlockerRequests = async (status = '') => {
    const params = {};
    if (status) params.status = status;
    return sendRequest({
      url: '/api/v1/internalBooking/blockerRequest',
      method: 'GET',
      headers: authCtx.getAuthHeaders(),
      params,
    });
  };

  const approveBlockerRequest = async requestId => {
    return sendRequest({
      url: `/api/v1/internalBooking/blockerRequest/${requestId}/approve`,
      method: 'POST',
      headers: authCtx.getAuthHeaders(),
      body: {},
    });
  };

  const rejectBlockerRequest = async (requestId, reason = '') => {
    const body = {};
    if (reason) body.reason = reason;
    return sendRequest({
      url: `/api/v1/internalBooking/blockerRequest/${requestId}/reject`,
      method: 'POST',
      headers: authCtx.getAuthHeaders(),
      body,
    });
  };

  // Overview of all current & upcoming timeslot blockers.
  const getBlockers = async () => {
    return sendRequest({
      url: '/api/v1/internalBooking/blockers',
      method: 'GET',
      headers: authCtx.getAuthHeaders(),
    });
  };

  const deleteBlocker = async internalBookingId => {
    return sendRequest({
      url: `/api/v1/internalBooking/blocker/${internalBookingId}`,
      method: 'DELETE',
      headers: authCtx.getAuthHeaders(),
    });
  };

  return {
    isLoading,
    error,
    getBookingsByEntertainmentDay,
    getFilteredBookingsByEntertainmentDay,
    getResourcesByEntertainmentsDay,
    getResourcesBySingleEntertainmentDay,
    updateBooking,
    createTimeslotBlocker,
    previewTimeslotBlockerSnap,
    getTimeslotBlockerTimeOptions,
    createBlockerRequest,
    getBlockerRequests,
    approveBlockerRequest,
    rejectBlockerRequest,
    getBlockers,
    deleteBlocker,
  };
};

export default useBooking;
